import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex } from 'core/components';
import { formatDateTime } from 'core/util/dateUtils';
import { ProductSkeleton } from 'core/components/Skeletons';
import UserMessage from 'app/views/core/kaizen/components/UserMessage';
import AgentMessage from 'app/views/core/kaizen/components/AgentMessage';
import ThinkingIndicator from 'app/views/core/kaizen/components/ThinkingIndicator';
import McpAuthRequiredBanner from 'app/views/core/kaizen/components/McpAuthRequiredBanner';
import { parseKaizenAnchorHash } from './util/anchorHash';
import { isScrollDownKey, isTouchScrollUp } from './util/scrollDirection';
import { ChatContainer, HASH_HIGHLIGHT_CLASS_NAME, MessagesWrapper } from './KaizenChatSession.styles';

// distance from the top of the scroll area to a newly sent message
const PINNED_MESSAGE_TOP_OFFSET = 20;

const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'touchmove', 'keydown', 'mousedown'];

// how close (in px) to the end of the chat still counts as being scrolled to it
const AT_END_THRESHOLD = 2;

// keys that scroll the view when focus isn't in a control that handles them itself
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

const isEditableTarget = (target) =>
  !!target?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"]');

@inject('$auth')
@observer
class KaizenChatSession extends Component {
  chatContainerRef = React.createRef();

  latestUserMessageRef = React.createRef();

  // id of the message currently scrolled to the top of the view
  pinnedMessageId = null;

  isFollowingEnd = false;

  // vertical position of the current touch, to tell which way it scrolls
  lastTouchY = undefined;

  hasAppliedHashScroll = false;

  hashHighlightTimeout = null;

  state = {
    stepReasoningOpenState: {},
    // while a message is pinned, keeps the chat tall enough for it to sit at the top of the view
    pinnedMinHeight: 0
  };

  constructor(props) {
    super(props);

    // Bind methods to avoid creating new functions on each render
    this.handleToggleReasoning = this.handleToggleReasoning.bind(this);
    this.handleSelectToolResult = this.handleSelectToolResult.bind(this);
    this.formatDate = this.formatDate.bind(this);
  }

  componentDidMount() {
    const { chatSessionModel, isExportMode } = this.props;

    // Skip auto-scroll behavior in export mode — no interactive viewport
    if (isExportMode) {
      return;
    }

    // Initial scroll for existing messages when component mounts
    this.renderedMessageCount = (chatSessionModel?.get('messages') || []).length;
    if (this.renderedMessageCount > 0) {
      this.applyNewMessageScroll({ isInitial: true });
    }

    // Keep the pinned min height (and the end-of-chat position) in sync as messages render/stream in or the
    // viewport resizes. The chat container catches content coming and going at the end (e.g. the thinking
    // indicator), but not while it's held at the pinned min height, so observe the messages too.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleContentResize);
      this.observedElements = new WeakSet();
      if (this.scrollContainer) {
        this.resizeObserver.observe(this.scrollContainer);
      }
      if (this.chatContainerRef.current) {
        this.resizeObserver.observe(this.chatContainerRef.current);
      }
      this.observeMessageElements();
    }

    // stop holding the view at the end of the chat as soon as the user scrolls themselves, and pick it back up
    // once they scroll back down to the end
    if (this.scrollContainer) {
      USER_SCROLL_EVENTS.forEach((type) =>
        this.scrollContainer.addEventListener(type, this.handleUserScroll, { passive: true })
      );
      this.scrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    this.applyHashAnchorScrollIfNeeded();
  }

  // React to new messages here rather than in a MobX reaction: sending a prompt happens inside a React event
  // handler, where the re-render is batched until after a reaction would run, so the new message wouldn't be
  // in the DOM yet to measure. componentDidUpdate always runs after it has been rendered.
  componentDidUpdate() {
    const { chatSessionModel, isExportMode } = this.props;
    const messageCount = (chatSessionModel?.get('messages') || []).length;

    if (isExportMode) {
      return;
    }

    this.observeMessageElements();

    if (messageCount === this.renderedMessageCount) {
      return;
    }

    this.renderedMessageCount = messageCount;
    this.applyNewMessageScroll();
    this.applyHashAnchorScrollIfNeeded();
  }

  componentWillUnmount() {
    const { chatSessionModel } = this.props;
    if (chatSessionModel) {
      chatSessionModel.onAgentStep = undefined;
      chatSessionModel.onToolResult = undefined;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.contentResizeFrame) {
      cancelAnimationFrame(this.contentResizeFrame);
      this.contentResizeFrame = null;
    }
    if (this.scrollContainer) {
      USER_SCROLL_EVENTS.forEach((type) => this.scrollContainer.removeEventListener(type, this.handleUserScroll));
      this.scrollContainer.removeEventListener('scroll', this.handleScroll);
    }
    if (this.hashHighlightTimeout) {
      clearTimeout(this.hashHighlightTimeout);
      this.hashHighlightTimeout = null;
    }
  }

  observeMessageElements() {
    if (!this.resizeObserver || !this.chatContainerRef.current) {
      return;
    }

    this.chatContainerRef.current.querySelectorAll('[data-message-index]').forEach((element) => {
      if (!this.observedElements.has(element)) {
        this.observedElements.add(element);
        this.resizeObserver.observe(element);
      }
    });
  }

  get scrollContainer() {
    return this.chatContainerRef.current?.parentElement;
  }

  applyNewMessageScroll = ({ isInitial = false } = {}) => {
    if (typeof window !== 'undefined' && parseKaizenAnchorHash(window.location.hash)) {
      return;
    }

    if (!this.chatContainerRef?.current || !this.scrollContainer) {
      return; // Exit if the ref is not yet set
    }

    const { chatSessionModel } = this.props;
    const messages = chatSessionModel?.get('messages') || [];
    const lastMessage = messages[messages.length - 1];

    // A just-sent prompt has no agent turn yet: scroll it to the top so the response streams in below it.
    // Anything else (e.g. opening an existing chat) just shows the end of the conversation. On mount that's
    // only the case for a brand new chat -- a reopened chat can briefly look like that from stale local state.
    const isAwaitingResponse = lastMessage && !lastMessage.agentTurn?.length;
    const isNewChat = messages.length === 1 && chatSessionModel?.get('isThinking');

    if (isAwaitingResponse && (!isInitial || isNewChat)) {
      this.pinMessageToTop(lastMessage.id);
    } else {
      this.scrollToEnd();
    }
  };

  pinMessageToTop = (messageId) => {
    this.pinnedMessageId = messageId;
    // once the response outgrows the view, follow it (see followResponse)
    this.isFollowingEnd = true;

    // size the chat for the (already rendered) message, then scroll once that has rendered too.
    // No requestAnimationFrame here: setState callbacks run after the DOM is updated, and frames are paused
    // in background tabs.
    this.updatePinnedMinHeight(() => this.scrollMessageToTop(messageId));
  };

  scrollMessageToTop(messageId) {
    // the component might unmount, or another message get pinned, before this runs
    const messageElement = this.getMessageElement(messageId);
    const { scrollContainer } = this;
    if (this.pinnedMessageId !== messageId || !messageElement || !scrollContainer) {
      return;
    }

    scrollContainer.scrollTo({
      top: this.getScrollOffsetOf(messageElement) - PINNED_MESSAGE_TOP_OFFSET,
      // background tabs don't run smooth scrolling, which would leave the prompt where it was
      behavior: document.hidden ? 'auto' : 'smooth'
    });
  }

  // Position of an element within the scrollable content, independent of the current scroll position. Uses layout
  // offsets rather than getBoundingClientRect so the messages' fade-in (a transform) doesn't skew it.
  getScrollOffsetOf(element) {
    const getDocumentOffset = (node) => {
      let offset = 0;
      for (let current = node; current; current = current.offsetParent) {
        offset += current.offsetTop;
      }
      return offset;
    };
    const { scrollContainer } = this;

    return getDocumentOffset(element) - getDocumentOffset(scrollContainer) - scrollContainer.clientTop;
  }

  scrollToEnd = () => {
    this.pinnedMessageId = null;
    // messages keep growing for a bit after they mount (markdown, charts), so follow them until the user scrolls
    this.isFollowingEnd = true;

    this.setState({ pinnedMinHeight: 0 }, this.jumpToEnd);
  };

  jumpToEnd = () => {
    if (this.scrollContainer) {
      this.scrollContainer.scrollTop = this.scrollContainer.scrollHeight;
    }
  };

  // Only input that actually scrolls counts; clicking or pressing keys on the chat's own controls
  // (links, expanding reasoning, etc.) shouldn't stop following the end.
  handleUserScroll = (event) => {
    if (this.isUserScrollEvent(event)) {
      this.isFollowingEnd = false;
    }
  };

  handleScroll = () => {
    const { scrollContainer } = this;

    if (!this.isFollowingEnd && scrollContainer) {
      const distanceFromEnd = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
      this.isFollowingEnd = distanceFromEnd <= AT_END_THRESHOLD;
    }
  };

  isUserScrollEvent(event) {
    const { scrollContainer } = this;

    if (event.type === 'mousedown') {
      // the scrollbar belongs to the scroll container itself and sits past its client area
      return event.target === scrollContainer && event.offsetX >= scrollContainer.clientWidth;
    }

    // Scrolling down never moves away from the newest content. At the end it doesn't move the view either, so no
    // scroll event would pick following back up.
    if (event.type === 'wheel') {
      return event.deltaY < 0;
    }

    if (event.type === 'touchstart') {
      this.lastTouchY = event.touches[0]?.clientY;
      return false;
    }

    if (event.type === 'touchmove') {
      const previousY = this.lastTouchY;
      this.lastTouchY = event.touches[0]?.clientY;
      return isTouchScrollUp(previousY, this.lastTouchY);
    }

    if (event.type === 'keydown') {
      if (!SCROLL_KEYS.has(event.key) || isScrollDownKey(event) || isEditableTarget(event.target)) {
        return false;
      }
      // space activates buttons and links rather than scrolling
      return event.key !== ' ' || !event.target?.closest?.('button, a, [role="button"]');
    }

    return true;
  }

  /**
   * Handling a resize changes the chat container's min height, and the container is observed too. Doing that inside
   * the observer callback makes the browser report "ResizeObserver loop completed with undelivered notifications",
   * so measure in the next frame instead, once per frame however many elements resized.
   */
  scheduleContentResize = () => {
    if (this.contentResizeFrame) {
      cancelAnimationFrame(this.contentResizeFrame);
    }

    this.contentResizeFrame = requestAnimationFrame(() => {
      this.contentResizeFrame = null;
      this.handleContentResize();
    });
  };

  handleContentResize = () => {
    if (this.pinnedMessageId) {
      // only matters if content above the pinned message changed size, or the viewport did
      this.updatePinnedMinHeight(this.followResponse);
    } else if (this.isFollowingEnd) {
      this.jumpToEnd();
    }
  };

  /**
   * Keeps the newest part of the response to the pinned message in view as steps and tool calls come in. Until the
   * response reaches the bottom of the view the pinned position already shows all of it, so this only scrolls once
   * it has outgrown the view. The final answer arrives in one piece, so stop at its top rather than its end.
   */
  followResponse = () => {
    const { scrollContainer } = this;
    const messageElement = this.pinnedMessageId && this.getMessageElement(this.pinnedMessageId);

    if (!this.isFollowingEnd || !scrollContainer || !messageElement) {
      return;
    }

    const pinnedScrollTop = this.getScrollOffsetOf(messageElement) - PINNED_MESSAGE_TOP_OFFSET;
    const finalAnswers = messageElement.querySelectorAll('[data-final-answer]');
    const finalAnswer = finalAnswers[finalAnswers.length - 1];
    let targetScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;

    if (finalAnswer) {
      targetScrollTop = Math.min(targetScrollTop, this.getScrollOffsetOf(finalAnswer) - PINNED_MESSAGE_TOP_OFFSET);
    }

    // only ever move forward, and leave the pinned position (and its smooth scroll) alone until there's more to show
    if (targetScrollTop > pinnedScrollTop + 1 && targetScrollTop > scrollContainer.scrollTop + 1) {
      scrollContainer.scrollTop = targetScrollTop;
    }
  };

  getMessageElement(messageId) {
    return this.chatContainerRef.current?.querySelector(`[id="kz-message-${messageId}"]`);
  }

  /**
   * Keeps the chat container tall enough that the pinned message can be scrolled to the top of the view.
   * This only depends on the message's position and the viewport height -- not on the content below the
   * message -- so the response streaming in, the thinking indicator coming and going, etc. can't shrink the
   * page out from under the pinned position. Once the response is taller than the view it just overflows.
   */
  updatePinnedMinHeight = (callback) => {
    const { pinnedMinHeight } = this.state;
    const { scrollContainer } = this;
    const chatContainer = this.chatContainerRef.current;
    const messageElement = this.pinnedMessageId && this.getMessageElement(this.pinnedMessageId);
    let nextMinHeight = 0;

    if (scrollContainer && chatContainer && messageElement) {
      const targetScrollTop = this.getScrollOffsetOf(messageElement) - PINNED_MESSAGE_TOP_OFFSET;
      const containerStyle = window.getComputedStyle(chatContainer);
      // min-height excludes padding unless the container uses border-box sizing
      const verticalPadding =
        containerStyle.boxSizing === 'border-box'
          ? 0
          : (parseFloat(containerStyle.paddingTop) || 0) + (parseFloat(containerStyle.paddingBottom) || 0);

      nextMinHeight = Math.max(
        0,
        Math.ceil(
          targetScrollTop + scrollContainer.clientHeight - this.getScrollOffsetOf(chatContainer) - verticalPadding
        )
      );
    }

    // ignore sub-pixel changes so resize observations can't loop
    if (Math.abs(nextMinHeight - pinnedMinHeight) < 1) {
      if (callback) {
        callback();
      }
      return;
    }

    this.setState({ pinnedMinHeight: nextMinHeight }, callback);
  };

  applyHashAnchorScrollIfNeeded = () => {
    if (this.hasAppliedHashScroll || typeof window === 'undefined') {
      return;
    }

    const parsedHash = parseKaizenAnchorHash(window.location.hash);
    if (!parsedHash) {
      return;
    }

    const targetElement = document.getElementById(parsedHash.anchorId);
    if (!targetElement) {
      return;
    }

    const scrollElement =
      parsedHash.kind === 'turn' ? targetElement.closest('[id^="kz-message-"]') || targetElement : targetElement;
    const scrollBlock = parsedHash.kind === 'turn' ? 'start' : 'center';

    this.hasAppliedHashScroll = true;
    scrollElement.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
    targetElement.classList.add(HASH_HIGHLIGHT_CLASS_NAME);

    this.hashHighlightTimeout = setTimeout(() => {
      targetElement.classList.remove(HASH_HIGHLIGHT_CLASS_NAME);
      this.hashHighlightTimeout = null;
    }, 2000);
  };

  handleSelectToolResult = (tool) => {
    const { onSelectToolResult } = this.props;
    if (onSelectToolResult) {
      onSelectToolResult(tool);
    }
  };

  handleToggleReasoning = (stepId) => {
    this.setState((prevState) => ({
      stepReasoningOpenState: {
        ...prevState.stepReasoningOpenState,
        [stepId]: !(prevState.stepReasoningOpenState[stepId] ?? true)
      }
    }));
  };

  handleShowAgentDataInSidebar = (turn, steps) => {
    // eslint-disable-next-line no-console
    console.table(turn);
    // eslint-disable-next-line no-console
    console.table(steps);
  };

  formatDate = (dateTime) => formatDateTime(new Date(dateTime), 'MMM DD, YYYY, hh:mm a');

  renderMessage(message) {
    const { stepReasoningOpenState } = this.state;
    const {
      $auth,
      chatSessionModel,
      isExistingChatLoading,
      isCopilotMode,
      inlineToolResults,
      isExportMode,
      onSendToExplorer
    } = this.props;
    const messages = chatSessionModel?.get('messages') || [];
    const messageIndex = messages.indexOf(message);
    const isLatestUserMessage = messageIndex === messages.length - 1 && message.message_text;

    const userMessage = <UserMessage message={message} formatDate={this.formatDate} />;
    const isAsker = (message.user_id ?? message.user?.id) === $auth.getActiveUserProperty('id');

    const agentMessages = message.agentTurn?.map((turn) => (
      <AgentMessage
        key={`agent-${turn.id}`}
        turn={turn}
        isAsker={isAsker}
        askerName={message.user?.user_full_name}
        stepReasoningOpenState={stepReasoningOpenState}
        onToggleReasoning={this.handleToggleReasoning}
        onSelectToolResult={this.handleSelectToolResult}
        onSendToExplorer={onSendToExplorer}
        onShowAgentDataInSidebar={this.handleShowAgentDataInSidebar}
        formatDate={this.formatDate}
        isCopilotMode={isCopilotMode}
        inlineToolResults={inlineToolResults}
        isExistingChatLoading={isExistingChatLoading}
        sessionModel={chatSessionModel}
      />
    ));

    return (
      <MessagesWrapper
        key={message.id}
        id={`kz-message-${message.id}`}
        ref={isLatestUserMessage ? this.latestUserMessageRef : null}
        data-message-index={messageIndex}
        isExportMode={isExportMode}
      >
        {userMessage}
        {agentMessages}
      </MessagesWrapper>
    );
  }

  render() {
    const { chatSessionModel, isCopilotMode, isExistingChatLoading, isExportMode } = this.props;
    const { pinnedMinHeight } = this.state;
    const messages = chatSessionModel?.get('messages') || [];
    // Same signal as the chat list: the live heartbeat (or optimistic send) rather than per-step state, which
    // can read as still in flight after a sub-agent has finished.
    const shouldShowThinking = chatSessionModel?.isAgentActive;
    const authRequiredTools = chatSessionModel?.get('authRequiredTools');

    return (
      <ChatContainer
        ref={this.chatContainerRef}
        isCopilotMode={isCopilotMode}
        isExportMode={isExportMode}
        // room to scroll a newly sent message to the top of the view -- skip in export mode
        style={!isExportMode && pinnedMinHeight > 0 ? { minHeight: pinnedMinHeight } : undefined}
      >
        {messages.length === 0 && isExistingChatLoading && (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="16px" height="100%" flex={1}>
            <ProductSkeleton width="30%" height="16px" style={{ alignSelf: 'flex-end' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-end' }} />
            <Flex height={50} />
            <ProductSkeleton width="30%" height="16px" style={{ alignSelf: 'flex-start' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-start' }} />
            <ProductSkeleton width="75%" height="30px" style={{ alignSelf: 'flex-start' }} />
          </Flex>
        )}

        {messages.map((message) => this.renderMessage(message))}
        {authRequiredTools?.length > 0 && !isExportMode && (
          <McpAuthRequiredBanner authRequiredTools={authRequiredTools} />
        )}
        {shouldShowThinking && <ThinkingIndicator />}
      </ChatContainer>
    );
  }
}

export default KaizenChatSession;
